import React, { Fragment, useState, FC, useEffect } from 'react';
import { toast, ToastType, LoadingIndicator, Sizes, IEBannerWarning } from '@chr/navisphereonline-shared-components';
import { loadAppConfig, UserProfile } from '@chr/online-shared-microsite-core';
import {
  readClassicUsersMap,
  mergeOnlineOneProfileToCustomerPlatformUser,
  readApplications,
  removeOnlineOneProfileFromCustomerPlatformUser } from '../../../common/api/products';
import { isEmptyString } from '@chr/online-shared-ui-validators';
import { UserDetail, Application } from 'appDtos';
import { ConfirmModal } from '../../../common/components/confirm-modal/confirm-modal';
import { AddedAccountDetail } from './added-account-detail';

import './merge-accounts.scss'
import { Translation, useTranslation } from '@chr/online-shared-ui-localizations';

export interface MergeAccountsProps {
    user: UserProfile;
}

export const MergeAccounts: FC<MergeAccountsProps> = ( { user }) => {
  const translate = useTranslation();
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordReminderPageUrl, setPasswordReminderPageUrl] = useState('');
  const [addedAccounts, setAddedAccounts] = useState([] as UserDetail[]);
  const [error, setError] = useState('');
  const [userNameRequired, setUserNameRequired] = useState(false);
  const [isLoading, setIsLoading ] = useState(false);
  const [isRemovingAccount, setIsRemovingAccount ] = useState(false);
  const [applications, setApplications] = useState([] as Application[])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [onlineOneUserId, setOnlineOneUserId] = useState(0);

  useEffect(() => {
    document.title = `${translate('Merge Accounts')} | C.H. Robinson Customer`;
  }, [translate])

  function sortStrings(a: string, b: string){
    if(a === null || b === null || a === '' || b === '') {
      return 0;
    }
    if(a.toUpperCase() > b.toUpperCase()) {
      return 1;
    }
    if( a.toUpperCase() < b.toUpperCase()) {
      return -1;
    }
    return 0;
  }

  function compareAccountsByName(a: UserDetail, b: UserDetail) {
    if(a.fullName.toUpperCase() === b.fullName.toUpperCase()){
      return sortStrings(a.userName, b.userName);
    }
    return sortStrings(a.fullName, b.fullName);
  }

  function compareAppNames(a: Application, b: Application){
    return sortStrings(a.name || '', b.name || '');
  }

  const getPasswordReminderPageUrl = async() => {
    var pageUrl = (await loadAppConfig() as AppConfig).legacyOnlinePasswordReminderPage;
    setPasswordReminderPageUrl(pageUrl);
  }

  const getAccounts = async() => {
    var accounts =  (await readClassicUsersMap(user.userId!)).onlineOneUsers as unknown as Array<UserDetail>;
    if (accounts !== undefined) {
      accounts.sort(compareAccountsByName);
    }
    setAddedAccounts(accounts);
  }

  const getApplications = async () => {
    var applications = await readApplications();
    if (applications && applications.length > 0) {
      applications.sort(compareAppNames);
      applications = applications.map( function(app){
        app.name = app.name?.toUpperCase();
        return app;
      });
    }
    setApplications(applications);
  }

  useEffect(() => {
    getApplications();
    getPasswordReminderPageUrl();
    getAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeOnlineOneProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if(!isEmptyString(error)) {
      setError('')
    }

    if (isEmptyString(userName)) {
      setUserNameRequired(true);
      setIsLoading(false);
      return;
    } else {
      setUserNameRequired(false);
    }

    if (isEmptyString(password)) {
      setError('Password required');
      setIsLoading(false);
      return;
    } else {
      setError('');
    }

    try
    {
      const errorMessage = await mergeOnlineOneProfileToCustomerPlatformUser(Number(user.userId), user.email!, userName, password);

      if(!isEmptyString(errorMessage)) {
        setError(errorMessage);
      } else {
        await getAccounts();
        await getApplications();
        setUserName('');
        setPassword('');
        toast(`Success! The account for ${userName} has been added to your C.H. Robinson single sign-on.`, ToastType.success);
      }
    } catch {
      toast(`There was a problem and your account was not added. Please try again. If the problem continues, please contact your C.H. Robinson representative.`, ToastType.error);
    }
    setIsLoading(false);
  }

  const openPasswordWindow = (e: React.FormEvent) => {
    e.preventDefault();
    window.open(passwordReminderPageUrl);
  }

  const changeUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUserName(e.target.value)
    if(!isEmptyString(e.target.value) && userNameRequired) {
      setUserNameRequired(false);
    }
  }

  const changePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPassword(e.target.value)
    if(!isEmptyString(e.target.value) && !isEmptyString(error)) {
      setError("");
    }
  }

  const toggleConfirmModal = async () => {
    setIsConfirmModalOpen(!isConfirmModalOpen);
    setIsRemovingAccount(true);
    try {
      await removeOnlineOneProfileFromCustomerPlatformUser(Number(user.userId), user.email!, onlineOneUserId);
      await getAccounts();
      await getApplications();
      toast(`Success! The account for ${userName} has been removed from your C.H. Robinson single sign-on.`, ToastType.success);
    } catch {
      toast(`There was a problem and your account was not removed. Please try again. If the problem continues, please contact your C.H. Robinson representative.`, ToastType.error);
    }
    setIsRemovingAccount(false);
  }

  const toggleCancelModal = () => {
    setIsConfirmModalOpen(!isConfirmModalOpen);
  }

  const removeAccount = async (onlineOneUserId: number) => {
    setOnlineOneUserId(onlineOneUserId);
    setIsConfirmModalOpen(true);
  }

  const displayHeading = () => {
    return (
      <div className="row">
        <IEBannerWarning/>
        <h1 className="header-title" data-testid="header-title">
          <Translation resourceKey='Merge Accounts'/>
        </h1>
        <div className="col-md-9">
          <Translation resourceKey='Legacy Platform is our original Navisphere customer application for global shipment visibility. If you have one or more accounts for Legacy Platform, use this tool to add them to your C.H. Robinson single sign-on for customer applications.'/>
        </div>
      </div>
    )
  };

  const displayAccountsFields = () => {
    return (
        <Fragment>
          <div className="col-sm-12 col-md-2 col-lg-2 padding-left-0">
            <h4>
              <b>
                <Translation resourceKey='Add a Legacy Platform Account' />
              </b>
            </h4>
            <form className="user-activation-form" onSubmit={mergeOnlineOneProfile} data-testid="form">
              <div className={`form-group`}>
                <label htmlFor="user-name">
                  <Translation resourceKey='Username' />
                </label>
                <input
                    className={`form-control ${userNameRequired ? 'required' : ''}`}
                    id="username"
                    title="Username"
                    value={userName}
                    placeholder={translate("Enter Username")}
                    data-testid="userName"
                    onChange={changeUserName}
                    autoComplete="new-password"
                    name="new-username"
                />
                {
                  userNameRequired &&
                    <span className="help-text ns-icon ns-error" title="userNameErrorMessage">
                      <Translation resourceKey='Username required' />
                    </span>
                }
              </div>
              <div className={`form-group`}>
                <label htmlFor="password">
                  <Translation resourceKey='Password' />
                </label>
                <input
                    className={`form-control ${error.length > 0  ? 'required' : ''}`}
                    id="password"
                    type="password"
                    title="Password"
                    value={password}
                    placeholder={translate("Enter Password")}
                    data-testid="password"
                    onChange={changePassword}
                    autoComplete="new-password"
                    name="new-password"
                />
                {
                  error.length > 0 &&
                  <span className="help-text ns-icon ns-error" title="errorMessage">
                    <Translation resourceKey={error} />
                  </span>
                }
              </div>
              <div className={`form-group`}>
              {
                isLoading ?
                  <LoadingIndicator size={Sizes.Small} />
                :
                  <button
                    className="btn btn-primary space-outer-top-lg"
                    type="submit"
                    id="btnSubmit"
                    data-testid="submitButton"
                  >
                    <Translation resourceKey='Add Account' />
                  </button>
              }
              </div>
              <div className={`form-group`}>
                <button
                  id="btnForgotPassword"
                  className="btn btn-link padding-left-0"
                  onClick={openPasswordWindow}>
                  <b>
                    <Translation resourceKey='Forgot Password' />
                  </b>
                </button>
              </div>
            </form>
          </div>
        </Fragment>
    )
  };

  const displayAddedAccounts = () => {
      if(addedAccounts && addedAccounts.length > 0) {
        return (
          <Fragment>
            {
              isRemovingAccount ?
                <div className="col-sm-7">
                  <LoadingIndicator size={Sizes.Large} />
                </div>
              :
            <div className="col-sm-12 col-md-10 col-lg-7">
              <div className="added-accounts-label-header">
              <h4>
                <b>
                  <Translation resourceKey='Accounts currently added' />
                </b>
              </h4>
              </div>
              <div className="added-accounts">
                <div className="added-accounts-header" data-testid="added-accounts-header">
                  {/* always visible */}
                  <div className="col-lg-3 col-md-3 col-sm-4 col-xs-4">
                    <Translation resourceKey='Name' />
                  </div>
                  <div className="col-lg-3 col-md-3 col-sm-4 col-xs-4">
                    <Translation resourceKey='User Name' />
                  </div>
                  {/* visible for md and lg */}
                  <div className="visible-md visible-lg col-lg-4 col-md-6 col-sm-4 col-xs-6">
                    <Translation resourceKey='Email' />
                  </div>
                </div>
                <div className="selected-account">
                  {
                    addedAccounts.map(account => {
                      return (
                        <div key={account.userName} className="row">
                          <div className="full-name">
                            <div className ="col-lg-3 col-md-3 col-sm-4 col-xs-4" data-testid='account-fullname'>
                              {account.fullName}
                            </div>
                          </div>
                          <div className="user-name">
                            <div className="col-lg-3 col-md-3 col-sm-4 col-xs-4">
                              {account.userName}
                            </div>
                          </div>
                          <div className="email">
                            <div className="email visible-md visible-lg col-lg-4 col-md-3 col-sm-12 col-xs-12">
                              {account.email}
                            </div>
                          </div>
                          <div className="visible-md visible-lg col-lg-2 col-md-3 col-sm-12 col-xs-12">
                          <button
                            id={`btn_${account.userId}`}
                            className="btn btn-link no-padding btn-Remove"
                            data-testid={`btn_${account.userId}`}
                            onClick={(e) => removeAccount(account.userId)}
                          >
                            <Translation resourceKey='Remove' />
                          </button>
                          </div>
                          <AddedAccountDetail account={account} isExpanded={false} removeAccount={removeAccount} data-testid="account-detail"/>
                        </div>
                      );
                    })
                  }
                    </div>
                  </div>
                  <div className="row margin-top-15">
                      <div className="col-sm-12">
                          <b>
                            <Translation resourceKey='These Legacy Platform accounts are included in your single sign-on. Use your single sign-on to access these accounts from now on.' />
                          </b>
                      </div>
                    </div>
                </div>
             }
            {
            isConfirmModalOpen &&
            (
              <ConfirmModal
                confirmResponse={toggleConfirmModal}
                cancelResponse={toggleCancelModal}
                confirmLabel={translate("OK")}
                cancelLabel={translate("Cancel")}
                title={translate('Remove Profile')}
                message={translate('You are about to remove a Legacy Platform account from your C.H. Robinson single sign-on.')}
              />
            )
          }
          </Fragment>
        )
      }

      else {
        return (
          <Fragment>
            <div className="col-sm-12 col-md-10 col-lg-7">
              <div className="added-accounts-label-header">
                <h4>
                  <b>
                    <Translation resourceKey='Accounts currently added' />
                  </b>
                </h4>
              </div>
              <div className="added-accounts">

                <div className="selected-account">
                  <p>
                    <Translation resourceKey='No accounts currently included.' />
                  </p>
                </div>
              </div>
            </div>
          </Fragment>
        );
      }
  };

  const displayWelcomeMessage = () => {
    if(applications && user){
      return (
        <Fragment>
          <div className="col-sm-12 col-md-12 col-lg-3">
            <div className="reminder-message border-left">
              <h5>
                <b>
                  <Translation resourceKey='Reminder: Your Single Sign-on' />
                </b>
              </h5>
              <p>
                <Translation resourceKey='Your single sign-on for C.H. Robinson Customer applications is' /> <strong>{user.email}</strong>.
              </p>
              <p>
                <Translation resourceKey='This single sign-on account  has access to the following applications:' />
              </p>
              <div>
                {applications.map(application => {
                  return (
                    <div key={application.applicationId}>
                      <strong data-testid="application-item">
                        {application.name}
                      </strong>
                    </div>
                    );
                  }
                  )
                }
              </div>
            </div>
          </div>
        </Fragment>
      )
    }
  };

    return (
    <>
      <div className="container-fluid">
        <div className="merge-accounts">
          <div className="row">
            {displayHeading()}
            <br/>
          </div>
          <div className="row">
            {displayAccountsFields()}
            {displayAddedAccounts()}
            {displayWelcomeMessage()}
          </div>
        </div>
      </div>
    </>
    );
}

  export default MergeAccounts;
